import { Tag } from './tag';

/**
 * Named tags for the attributes this application reads.
 *
 * The typed model is addressed by tag rather than by keyword, so these are
 * what call sites use in place of the string keys the naturalized shape needs.
 * A named constant rather than a hex literal at the call site: `0x00200032`
 * says nothing at a glance, and a transposed digit in one is a different
 * attribute rather than a compile error.
 *
 * Grouped by DICOM module so a reader can check a group against the standard
 * without reading the whole file.
 */

// SOP Common
export const SPECIFIC_CHARACTER_SET: Tag = 0x00080005;
export const SOP_CLASS_UID: Tag = 0x00080016;
export const SOP_INSTANCE_UID: Tag = 0x00080018;
export const INSTANCE_CREATION_DATE: Tag = 0x00080012;
export const INSTANCE_CREATION_TIME: Tag = 0x00080013;

// Patient
export const PATIENT_NAME: Tag = 0x00100010;
export const PATIENT_ID: Tag = 0x00100020;
export const PATIENT_BIRTH_DATE: Tag = 0x00100030;
export const PATIENT_SEX: Tag = 0x00100040;
export const PATIENT_WEIGHT: Tag = 0x00101030;
export const PATIENT_POSITION: Tag = 0x00185100;

// General Study / Series
export const STUDY_DATE: Tag = 0x00080020;
export const SERIES_DATE: Tag = 0x00080021;
export const ACQUISITION_DATE: Tag = 0x00080022;
export const STUDY_TIME: Tag = 0x00080030;
export const SERIES_TIME: Tag = 0x00080031;
export const ACQUISITION_TIME: Tag = 0x00080032;
export const ACCESSION_NUMBER: Tag = 0x00080050;
export const MODALITY: Tag = 0x00080060;
export const MANUFACTURER: Tag = 0x00080070;
export const STATION_NAME: Tag = 0x00081010;
export const SERIES_DESCRIPTION: Tag = 0x0008103e;
export const MANUFACTURER_MODEL_NAME: Tag = 0x00081090;
export const STUDY_INSTANCE_UID: Tag = 0x0020000d;
export const SERIES_INSTANCE_UID: Tag = 0x0020000e;
export const STUDY_ID: Tag = 0x00200010;
export const SERIES_NUMBER: Tag = 0x00200011;
export const INSTANCE_NUMBER: Tag = 0x00200013;
export const IMAGE_TYPE: Tag = 0x00080008;

// Frame of Reference
export const FRAME_OF_REFERENCE_UID: Tag = 0x00200052;

// Image Plane
export const SLICE_THICKNESS: Tag = 0x00180050;
export const SPACING_BETWEEN_SLICES: Tag = 0x00180088;
export const IMAGE_POSITION_PATIENT: Tag = 0x00200032;
export const IMAGE_ORIENTATION_PATIENT: Tag = 0x00200037;
export const SLICE_LOCATION: Tag = 0x00201041;
export const PIXEL_SPACING: Tag = 0x00280030;

// Image Pixel
export const ROWS: Tag = 0x00280010;
export const COLUMNS: Tag = 0x00280011;
export const BITS_ALLOCATED: Tag = 0x00280100;
export const BITS_STORED: Tag = 0x00280101;
export const HIGH_BIT: Tag = 0x00280102;
export const PIXEL_REPRESENTATION: Tag = 0x00280103;
export const WINDOW_CENTER: Tag = 0x00281050;
export const WINDOW_WIDTH: Tag = 0x00281051;
export const RESCALE_INTERCEPT: Tag = 0x00281052;
export const RESCALE_SLOPE: Tag = 0x00281053;

// CT / MR / PET acquisition
export const KVP: Tag = 0x00180060;
export const FILTER_TYPE: Tag = 0x00181160;
export const CTDI_VOL: Tag = 0x00189345;
export const CONVOLUTION_KERNEL: Tag = 0x00181210;
export const PROTOCOL_NAME: Tag = 0x00181030;
export const SCANNING_SEQUENCE: Tag = 0x00180020;
export const SEQUENCE_VARIANT: Tag = 0x00180021;
export const MR_ACQUISITION_TYPE: Tag = 0x00180023;
export const SEQUENCE_NAME: Tag = 0x00180024;
export const REPETITION_TIME: Tag = 0x00180080;
export const ECHO_TIME: Tag = 0x00180081;
export const INVERSION_TIME: Tag = 0x00180082;
export const NUMBER_OF_AVERAGES: Tag = 0x00180083;
export const MAGNETIC_FIELD_STRENGTH: Tag = 0x00180087;
export const FLIP_ANGLE: Tag = 0x00181314;
export const RADIOPHARMACEUTICAL_INFORMATION_SEQUENCE: Tag = 0x00540016;
export const RADIOPHARMACEUTICAL: Tag = 0x00180031;
export const RADIONUCLIDE_TOTAL_DOSE: Tag = 0x00181074;
export const RADIONUCLIDE_HALF_LIFE: Tag = 0x00181075;
export const RADIOPHARMACEUTICAL_START_TIME: Tag = 0x00181072;
export const RADIOPHARMACEUTICAL_START_DATETIME: Tag = 0x00181078;

// RT Structure Set
export const STRUCTURE_SET_LABEL: Tag = 0x30060002;
export const STRUCTURE_SET_NAME: Tag = 0x30060004;
export const STRUCTURE_SET_DESCRIPTION: Tag = 0x30060006;
export const STRUCTURE_SET_DATE: Tag = 0x30060008;
export const STRUCTURE_SET_TIME: Tag = 0x30060009;
export const REFERENCED_FRAME_OF_REFERENCE_SEQUENCE: Tag = 0x30060010;
export const RT_REFERENCED_STUDY_SEQUENCE: Tag = 0x30060012;
export const RT_REFERENCED_SERIES_SEQUENCE: Tag = 0x30060014;
export const CONTOUR_IMAGE_SEQUENCE: Tag = 0x30060016;
export const STRUCTURE_SET_ROI_SEQUENCE: Tag = 0x30060020;
export const ROI_NUMBER: Tag = 0x30060022;
export const REFERENCED_FRAME_OF_REFERENCE_UID: Tag = 0x30060024;
export const ROI_NAME: Tag = 0x30060026;
export const ROI_DESCRIPTION: Tag = 0x30060028;
export const ROI_GENERATION_ALGORITHM: Tag = 0x30060036;
export const ROI_CONTOUR_SEQUENCE: Tag = 0x30060039;
export const ROI_DISPLAY_COLOR: Tag = 0x3006002a;
export const CONTOUR_SEQUENCE: Tag = 0x30060040;
export const CONTOUR_GEOMETRIC_TYPE: Tag = 0x30060042;
export const NUMBER_OF_CONTOUR_POINTS: Tag = 0x30060046;
export const CONTOUR_DATA: Tag = 0x30060050;
export const RT_ROI_OBSERVATIONS_SEQUENCE: Tag = 0x30060080;
export const OBSERVATION_NUMBER: Tag = 0x30060082;
export const REFERENCED_ROI_NUMBER: Tag = 0x30060084;
export const ROI_OBSERVATION_LABEL: Tag = 0x30060085;
export const RT_ROI_INTERPRETED_TYPE: Tag = 0x300600a4;
export const ROI_INTERPRETER: Tag = 0x300600a6;
export const APPROVAL_STATUS: Tag = 0x300e0002;
export const REVIEW_DATE: Tag = 0x300e0004;
export const REVIEW_TIME: Tag = 0x300e0005;
export const REVIEWER_NAME: Tag = 0x300e0008;

// References shared across RT objects
export const REFERENCED_SOP_CLASS_UID: Tag = 0x00081150;
export const REFERENCED_SOP_INSTANCE_UID: Tag = 0x00081155;
export const REFERENCED_RT_PLAN_SEQUENCE: Tag = 0x300c0002;
export const REFERENCED_STRUCTURE_SET_SEQUENCE: Tag = 0x300c0060;
export const SOFTWARE_VERSIONS: Tag = 0x00181020;

// RT Dose
export const DOSE_UNITS: Tag = 0x30040002;
export const DOSE_TYPE: Tag = 0x30040004;
export const DOSE_COMMENT: Tag = 0x30040006;
export const DOSE_SUMMATION_TYPE: Tag = 0x3004000a;
export const GRID_FRAME_OFFSET_VECTOR: Tag = 0x3004000c;
export const DOSE_GRID_SCALING: Tag = 0x3004000e;
export const NUMBER_OF_FRAMES: Tag = 0x00280008;

// RT Dose Volume Histogram
export const DVH_SEQUENCE: Tag = 0x30040050;
export const DVH_REFERENCED_ROI_SEQUENCE: Tag = 0x30040060;
export const DVH_ROI_CONTRIBUTION_TYPE: Tag = 0x30040062;
export const DVH_TYPE: Tag = 0x30040001;
export const DVH_DOSE_SCALING: Tag = 0x30040052;
export const DVH_VOLUME_UNITS: Tag = 0x30040054;
export const DVH_NUMBER_OF_BINS: Tag = 0x30040056;
export const DVH_DATA: Tag = 0x30040058;
export const DVH_MINIMUM_DOSE: Tag = 0x30040070;
export const DVH_MAXIMUM_DOSE: Tag = 0x30040072;
export const DVH_MEAN_DOSE: Tag = 0x30040074;

// Spatial Registration
export const CONTENT_LABEL: Tag = 0x00700080;
export const REGISTRATION_SEQUENCE: Tag = 0x00700308;
export const DEFORMABLE_REGISTRATION_SEQUENCE: Tag = 0x00640002;
export const SOURCE_FRAME_OF_REFERENCE_UID: Tag = 0x00640003;
export const DEFORMABLE_REGISTRATION_GRID_SEQUENCE: Tag = 0x00640005;
export const GRID_DIMENSIONS: Tag = 0x00640007;
export const GRID_RESOLUTION: Tag = 0x00640008;
export const VECTOR_GRID_DATA: Tag = 0x00640009;
export const PRE_DEFORMATION_MATRIX_REGISTRATION_SEQUENCE: Tag = 0x0064000f;
export const POST_DEFORMATION_MATRIX_REGISTRATION_SEQUENCE: Tag = 0x00640010;
export const MATRIX_REGISTRATION_SEQUENCE: Tag = 0x00700309;
export const MATRIX_SEQUENCE: Tag = 0x0070030a;
export const FRAME_OF_REFERENCE_TRANSFORMATION_MATRIX: Tag = 0x300600c6;
